<!-- ── Space Calendar ──────────────────────────────────────────────────── -->
<!-- AGENTS.md: overflow-x-hidden on top-level container -->
<div class="min-h-screen w-full min-w-0 overflow-x-hidden bg-cloud-mist">

  <div class="tfr-page-shell-wide pb-28 lg:pb-8">
  <!-- ── Page header (inline, not sticky — Layout provides the chrome) ── -->
  <div class="flex min-w-0 items-start gap-3 pt-4 sm:items-center sm:pt-5 pb-2 sm:pb-3">

    <button type="button" (click)="goBack()"
      aria-label="Volver a Mis Espacios"
      class="shrink-0 flex items-center justify-center h-9 w-9 rounded-xl border transition-colors focus:outline-none border-pale-gray text-slate-blue bg-snow-white">
      <lucide-icon name="arrow-left" [size]="16" />
    </button>

    @if (loading()) {
      <div class="h-5 w-44 rounded-lg animate-pulse bg-pale-gray flex-1"></div>
    } @else {
      <div class="flex-1 min-w-0">
        <h1 class="text-[14px] sm:text-[15px] font-bold tracking-tight truncate text-midnight-indigo">
          {{ space()?.name ?? 'Espacio' }}
        </h1>
        <p class="text-[10px] sm:text-[11px] mt-0.5 leading-snug text-steel-gray line-clamp-2 sm:line-clamp-none">
          Hoy &mdash; 1 a&ntilde;o &middot; bloqueos manuales
        </p>
      </div>
    }

    <!-- Status legend -->
    <div class="hidden sm:flex items-center gap-3 text-[10px] font-medium text-steel-gray">
      <span class="flex items-center gap-1.5">
        <span class="h-2 w-2 rounded-full bg-green-400 shrink-0"></span>Libre
      </span>
      <span class="flex items-center gap-1.5">
        <span class="h-2 w-2 rounded-full bg-steel-gray shrink-0"></span>Parcial
      </span>
      <span class="flex items-center gap-1.5">
        <span class="h-2 w-2 rounded-full bg-red-400 shrink-0"></span>Completo
      </span>
    </div>

  </div>

  <!-- ── Main layout (mini calendar first on mobile) ─────────────────── -->
  <div class="flex min-w-0 flex-col gap-4 py-3 sm:py-5 lg:flex-row lg:gap-5">

    <!-- ── LEFT: mini calendar (sidebar on desktop) ──────────────────── -->
    <aside class="order-1 lg:order-1 w-full lg:w-[248px] shrink-0 min-w-0">

      <!-- Mini-calendar: one month + navigation -->
      <div class="bg-white rounded-2xl border p-3 sm:p-4 border-pale-gray shadow-tfr-sm min-w-0 w-full max-w-full box-border">

        <div class="mb-3 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <button
              type="button"
              (click)="goToToday()"
              [disabled]="isOnDefaultView()"
              aria-label="Ir a la semana de hoy"
              class="h-8 px-2.5 flex items-center gap-1 rounded-lg border text-[11px] font-semibold transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-default border-pale-gray text-slate-blue shrink-0">
              <lucide-icon name="calendar-days" [size]="12" />
              Hoy
            </button>

            <div class="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                (click)="prevMiniMonth()"
                [disabled]="!canPrevMiniMonth()"
                aria-label="Mes anterior"
                class="h-8 w-8 flex items-center justify-center rounded-lg border transition-colors focus:outline-none border-pale-gray text-slate-blue disabled:opacity-40 disabled:cursor-default">
                <lucide-icon name="chevron-left" [size]="14" />
              </button>
              <button
                type="button"
                (click)="nextMiniMonth()"
                [disabled]="!canNextMiniMonth()"
                aria-label="Mes siguiente"
                class="h-8 w-8 flex items-center justify-center rounded-lg border transition-colors focus:outline-none border-pale-gray text-slate-blue disabled:opacity-40 disabled:cursor-default">
                <lucide-icon name="chevron-right" [size]="14" />
              </button>
            </div>
          </div>

          <p class="mt-2 text-center text-[12px] font-semibold tracking-tight text-midnight-indigo">
            {{ miniCalendar().label }}
          </p>
        </div>

        <div class="min-w-0 w-full">
        <!-- Day-of-week headers -->
        <div class="grid grid-cols-7 mb-1 gap-px">
          @for (h of ['L','M','X','J','V','S','D']; track h; let i = $index) {
            <span class="text-center text-[9px] font-semibold uppercase tracking-wider"
                  [style.color]="i >= 5 ? '#FDA4AF' : '#A6BBD1'">{{ h }}</span>
          }
        </div>

        <!-- Weeks -->
        <div class="flex flex-col gap-y-1">
          @for (week of miniCalendar().weeks; track trackByWeek($index, week)) {
            <div class="grid grid-cols-7 gap-px rounded-lg transition-colors"
                 [style.background]="isInDisplayedWeek(week[0].date) ? '#DBE7F2' : ''">
              @for (day of week; track trackByDay($index, day)) {
                <button type="button"
                  (click)="day.inMonth && isDateInViewRange(day.date) && goToWeekOf(day.date)"
                  [disabled]="!day.inMonth || !isDateInViewRange(day.date)"
                  class="flex flex-col items-center py-0.5 transition-all focus:outline-none bg-transparent"
                  [class.opacity-20]="!day.inMonth || !isDateInViewRange(day.date)"
                  [class.cursor-default]="!day.inMonth || !isDateInViewRange(day.date)">

                  <span class="h-5 w-5 flex items-center justify-center rounded-full text-[11px] font-medium leading-none"
                        [style.background]="day.inMonth && isToday(day.date) ? '#0B3558' : ''"
                        [style.color]="day.inMonth && isToday(day.date) ? '#fff' :
                                       day.inMonth ? '#0B3558' : '#D1D5DB'">
                    {{ day.date.getDate() }}
                  </span>

                  @if (day.inMonth && day.dot !== 'none') {
                    <span class="h-1.5 w-1.5 rounded-full -mt-0.5"
                          [class.bg-green-400]="day.dot === 'green'"
                          [class.bg-steel-gray]="day.dot === 'gray'"
                          [class.bg-red-400]="day.dot === 'red'"></span>
                  } @else {
                    <span class="h-1.5 -mt-0.5"></span>
                  }
                </button>
              }
            </div>
          }
        </div>
        </div>
      </div>

      <!-- Mobile legend (below mini calendar) -->
      <div class="flex lg:hidden items-center justify-center gap-3 sm:gap-4 text-[10px] font-medium mt-3 bg-white rounded-2xl border px-3 py-2 border-pale-gray text-steel-gray">
        <span class="flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-full bg-green-400 shrink-0"></span>Libre</span>
        <span class="flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-full bg-steel-gray shrink-0"></span>Parcial</span>
        <span class="flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-full bg-red-400 shrink-0"></span>Completo</span>
      </div>
    </aside>

    <!-- ── RIGHT: Weekly grid ──────────────────────────────────────── -->
    <section
      class="order-2 lg:order-2 flex-1 min-w-0 w-full max-w-full bg-white rounded-2xl border overflow-hidden border-pale-gray shadow-tfr-sm">

      <!-- Week navigator -->
      <div class="flex items-center justify-between gap-2 px-2 sm:px-4 py-2.5 sm:py-3 border-b border-pale-gray">
        <button type="button" (click)="prevWeek()"
          [disabled]="!canPrevWeek()"
          class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none border-pale-gray text-slate-blue disabled:opacity-40 disabled:cursor-default">
          <lucide-icon name="chevron-left" [size]="14" />
        </button>
        <span class="text-[11px] sm:text-[13px] font-semibold text-midnight-indigo text-center min-w-0 px-1 leading-tight">
          {{ formatWeekRange() }}
        </span>
        <button type="button" (click)="nextWeek()"
          [disabled]="!canNextWeek()"
          class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none border-pale-gray text-slate-blue disabled:opacity-40 disabled:cursor-default">
          <lucide-icon name="chevron-right" [size]="14" />
        </button>
      </div>

      <!-- AGENTS.md: overflow-x-auto with sticky time column (scroll contained — no page overflow) -->
      <div class="w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain max-h-[min(58vh,560px)] lg:max-h-none lg:overflow-x-visible">
        <div class="w-full min-w-[280px]">

          <!-- Column headers (schedule-step style) -->
          <div class="flex sticky top-0 z-20 bg-white border-b border-pale-gray w-full min-w-0">
            <div class="sticky left-0 z-30 flex items-center justify-center border-r py-2.5 sm:py-3 bg-white shadow-[4px_0_8px_-2px_rgba(11,53,88,0.08)]"
                 style="width:52px; flex-shrink:0; border-color:#E7EDF6;">
              <span class="text-[9px] sm:text-[10px] font-medium uppercase tracking-wider text-steel-gray">Hora</span>
            </div>
            <div class="flex-1 grid min-w-0 grid-cols-7">
              @for (date of weekDates(); track date.toISOString(); let i = $index; let last = $last) {
                <div
                     [class.border-r]="!last"
                     [style.background]="isToday(date) ? '#F8F9FB' : 'transparent'"
                     class="flex min-w-0 flex-col items-center justify-center py-2 sm:py-2.5 border-pale-gray">
                  <span class="text-[8px] sm:text-[9px] font-semibold uppercase tracking-wider text-steel-gray">
                    {{ DAYS_SHORT[i] }}
                  </span>
                  <span class="text-[12px] sm:text-[13px] font-bold mt-0.5 h-6 w-6 flex items-center justify-center rounded-full"
                        [style.background]="isToday(date) ? '#0B3558' : 'transparent'"
                        [style.color]="isToday(date) ? '#fff' : '#0B3558'">
                    {{ date.getDate() }}
                  </span>
                </div>
              }
            </div>
          </div>

          <!-- Grid body: merged event blocks with absolute positioning -->
          @if (loading()) {
            <div class="flex items-center justify-center py-20 text-steel-gray">
              <lucide-icon name="loader-circle" [size]="20" class="animate-spin mr-2" />
              <span class="text-[13px]">Cargando horarios&hellip;</span>
            </div>
          } @else if (weekGridData().slots.length === 0) {
            <div class="flex flex-col items-center justify-center py-16 text-center px-6">
              <lucide-icon name="clock" [size]="28" class="text-platinum-tint mb-3" />
              <p class="text-[13px] text-steel-gray">Sin horario configurado</p>
              <p class="text-[11px] mt-1 max-w-xs text-platinum-tint">
                A&ntilde;ade un horario de apertura y cierre en la configuraci&oacute;n del espacio.
              </p>
            </div>
          } @else {
            <!-- Time-label column + 7 day columns, all absolutely positioned -->
            <div class="relative flex w-full min-w-0"
                 [style.height.px]="weekGridData().slots.length * ROW_HEIGHT">

              <!-- Time label column — sticky on horizontal scroll -->
              <div class="sticky left-0 z-30 shrink-0 relative border-r bg-white shadow-[4px_0_8px_-2px_rgba(11,53,88,0.08)]"
                   style="width:52px; border-color:#E7EDF6;">
                @for (slot of weekGridData().slots; track slot.hour * 2 + slot.half; let si = $index) {
                  <div class="absolute left-0 right-0 border-b pointer-events-none"
                       [style.top.px]="si * ROW_HEIGHT"
                       [style.height.px]="ROW_HEIGHT"
                       [style.border-color]="slot.half === 1 ? '#E7EDF6' : '#F8F9FB'">
                  </div>
                  <span class="absolute right-1.5 leading-none pointer-events-none tabular-nums"
                        [style.color]="slot.half === 0 ? '#476788' : '#C4D0DF'"
                        style="font-size:9px; font-weight:500;"
                        [style.top.px]="si * ROW_HEIGHT + 5">
                    {{ formatHour(slot.hour, slot.half) }}
                  </span>
                }
              </div>

              <!-- ── Day columns (7) ─────────────────────────────── -->
              @for (col of weekGridData().columns; track col.date.toISOString(); let ci = $index) {
                <div
                     [style.background]="isToday(col.date) ? '#FAFBFD' : 'transparent'"
                     class="min-w-0 flex-1 relative border-r last:border-r-0 border-pale-gray">

                  <!-- Background grid lines (non-interactive) -->
                  @for (slot of weekGridData().slots; track slot.hour * 2 + slot.half; let si = $index) {
                    <div class="absolute left-0 right-0 border-b pointer-events-none"
                         [style.top.px]="si * ROW_HEIGHT"
                         [style.height.px]="ROW_HEIGHT"
                         [style.background-color]="slot.half === 1 ? 'rgba(247,249,251,0.7)' : 'transparent'"
                         [style.border-color]="slot.half === 1 ? '#E7EDF6' : '#F8F9FB'">
                    </div>
                  }

                  <!-- Inactive slots: past hours or days outside the block window -->
                  @for (slot of weekGridData().slots; track slot.hour * 2 + slot.half; let si = $index) {
                    @if (col.inactiveMask[si]) {
                      <div class="absolute left-0 right-0 pointer-events-none z-[3]"
                           style="background-image: repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(166,187,209,0.22) 4px, rgba(166,187,209,0.22) 8px);"
                           [style.top.px]="si * ROW_HEIGHT"
                           [style.height.px]="ROW_HEIGHT"></div>
                    }
                  }

                  <!-- Clickable free slots (hover hint) -->
                  @for (slot of weekGridData().slots; track slot.hour * 2 + slot.half; let si = $index) {
                    @if (col.freeMask[si] && !col.inactiveMask[si]) {
                      <div class="absolute left-0 right-0 cursor-pointer group z-[5]"
                           [style.top.px]="si * ROW_HEIGHT"
                           [style.height.px]="ROW_HEIGHT"
                           (click)="openBlockModal(col.date, slot.hour, slot.half)">
                        <!-- Released-fixed-block hint: persistent dashed border + plus -->
                        @if (col.releasedMask[si]) {
                          <div class="absolute inset-[2px] rounded border border-dashed flex items-center justify-center transition-colors"
                               style="border-color:#94A3B8; background:rgba(148,163,184,0.06);">
                            <lucide-icon name="plus" [size]="10" class="text-slate-blue" />
                          </div>
                        } @else {
                          <div class="absolute inset-[2px] rounded opacity-0 group-hover:opacity-100 transition-opacity
                                      flex items-center justify-center"
                               style="background:rgba(11,53,88,0.04);">
                            <lucide-icon name="plus" [size]="9" class="text-steel-gray" />
                          </div>
                        }
                      </div>
                    }
                  }

                  <!-- Merged event blocks -->
                  @for (event of col.events; track $index) {
                    <div class="absolute left-[3px] right-[3px] rounded-lg overflow-hidden z-[10] border-l-2 transition-opacity"
                         [class.cursor-pointer]="canRemoveCalendarEvent(event, col.date)"
                         [class.hover:opacity-80]="canRemoveCalendarEvent(event, col.date)"
                         [style.top.px]="event.startSlotIdx * ROW_HEIGHT + 2"
                         [style.height.px]="event.spanSlots * ROW_HEIGHT - 4"
                         [style.border-left-color]="event.borderColor"
                         [ngClass]="event.bgClass"
                         (click)="$event.stopPropagation();
                                  canRemoveCalendarEvent(event, col.date) && (
                                    isRecurringBlock(event)
                                      ? openReleaseModal(event, col.date)
                                      : openDeleteBlockModal(event, col.date)
                                  )">
                      <div class="px-1.5 pt-[3px] h-full flex flex-col overflow-hidden">
                        <!-- Icon + title row -->
                        <div class="flex items-center gap-0.5 min-w-0">
                          <lucide-icon [name]="event.iconName" [size]="8"
                                       class="shrink-0" [ngClass]="event.textClass" />
                          <span class="leading-none font-bold truncate flex-1 min-w-0"
                                style="font-size:8px;"
                                [ngClass]="event.textClass">
                            {{ event.title }}
                          </span>
                        </div>
                        <!-- Time range (shown only when block is tall enough) -->
                        @if (event.spanSlots >= 2) {
                          <span class="leading-none mt-[3px] opacity-60 truncate"
                                style="font-size:7px;"
                                [ngClass]="event.textClass">
                            {{ event.timeRange }}
                          </span>
                        }
                      </div>
                    </div>
                  }

                </div>
              }

            </div>
          }

        </div>
      </div>
    </section>
  </div>

  </div>

</div>

<!-- ── Manual Block Modal ──────────────────────────────────────────────── -->
@if (blockModalOpen()) {
  <div
      
       (click)="closeBlockModal()" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 tfr-modal-overlay">

    <div
        
         (click)="$event.stopPropagation()" class="bg-white rounded-2xl border w-full max-w-sm px-6 py-6 border-pale-gray shadow-tfr-sm">

      <!-- Header -->
      <div class="flex items-center justify-between mb-5">
        <div>
          <h3 class="text-[15px] font-bold tracking-tight text-midnight-indigo">Bloquear horario</h3>
          <p class="text-[11px] mt-0.5 text-steel-gray">El slot quedar&aacute; marcado como no disponible.</p>
        </div>
        <button type="button" (click)="closeBlockModal()"
         
          class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none border-pale-gray text-slate-blue">
          <lucide-icon name="x" [size]="14" />
        </button>
      </div>

      <!-- Form -->
      <div class="flex flex-col gap-3">

        <div>
          <label class="text-[11px] font-semibold mb-1 block text-slate-blue">Fecha</label>
          <input type="date"
            [ngModel]="blockForm().date"
            (ngModelChange)="updateBlockForm({ date: $event })"
            [min]="blockAllowedDate" class="w-full rounded-xl border px-3 py-2.5 text-[13px] focus:outline-none transition border-pale-gray text-midnight-indigo" />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="text-[11px] font-semibold mb-1 block text-slate-blue">Inicio</label>
            <div class="relative">
              <select
                [ngModel]="blockForm().startTime"
                (ngModelChange)="updateBlockForm({ startTime: $event })"
               
                class="w-full appearance-none rounded-xl border px-3 py-2.5 pr-8 text-[13px] focus:outline-none transition bg-white border-pale-gray text-midnight-indigo">
                @for (t of blockTimeOptions(); track t) {
                  <option [value]="t">{{ t }}</option>
                }
              </select>
              <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-steel-gray">
                <lucide-icon name="chevron-down" class="w-3 h-3" />
              </span>
            </div>
          </div>
          <div>
            <label class="text-[11px] font-semibold mb-1 block text-slate-blue">Fin</label>
            <div class="relative">
              <select
                [ngModel]="blockForm().endTime"
                (ngModelChange)="updateBlockForm({ endTime: $event })"
               
                class="w-full appearance-none rounded-xl border px-3 py-2.5 pr-8 text-[13px] focus:outline-none transition bg-white border-pale-gray text-midnight-indigo">
                @for (t of blockTimeOptions(); track t) {
                  <option [value]="t">{{ t }}</option>
                }
              </select>
              <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-steel-gray">
                <lucide-icon name="chevron-down" class="w-3 h-3" />
              </span>
            </div>
          </div>
        </div>

        <div>
          <label class="text-[11px] font-semibold mb-1 block text-slate-blue">Etiqueta (opcional)</label>
          <input type="text"
            [ngModel]="blockForm().label"
            (ngModelChange)="updateBlockForm({ label: $event })"
            placeholder="Ej: Mantenimiento, Evento privado&hellip;" class="w-full rounded-xl border px-3 py-2.5 text-[13px] placeholder:text-gray-300 focus:outline-none transition border-pale-gray text-midnight-indigo" />
        </div>

      </div>

      <!-- Actions -->
      <div class="flex gap-3 mt-6">
        <button type="button" (click)="closeBlockModal()"
         
          class="flex-1 rounded-xl border px-4 py-2.5 text-[13px] font-semibold transition-colors focus:outline-none border-pale-gray text-slate-blue">
          Cancelar
        </button>
        <button type="button" (click)="saveBlock()" [disabled]="blockSaving()"
         
          class="flex-1 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity disabled:opacity-60 disabled:cursor-wait focus:outline-none bg-midnight-indigo">
          @if (blockSaving()) { Guardando&hellip; } @else { Bloquear }
        </button>
      </div>

    </div>
  </div>
}

<!-- ── Delete Manual-Block Modal ───────────────────────────────────────── -->
@if (deleteTarget(); as target) {
  <div
      
       (click)="closeDeleteBlockModal()" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 tfr-modal-overlay">

    <div
        
         (click)="$event.stopPropagation()" class="bg-white rounded-2xl border w-full max-w-sm px-6 py-6 border-pale-gray shadow-tfr-sm">

      <!-- Header -->
      <div class="flex items-start justify-between mb-5">
        <div class="min-w-0">
          <h3 class="text-[15px] font-bold tracking-tight text-midnight-indigo">Eliminar bloqueo</h3>
          <p class="text-[11px] mt-0.5 text-steel-gray">El hueco volver&aacute; a quedar disponible para reservas.</p>
        </div>
        <button type="button" (click)="closeDeleteBlockModal()"
         
          class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none shrink-0 ml-3 border-pale-gray text-slate-blue">
          <lucide-icon name="x" [size]="14" />
        </button>
      </div>

      <!-- Summary card -->
      <div class="rounded-xl border p-4 mb-5 border-pale-gray bg-cloud-mist">
        <p class="text-[11px] font-semibold uppercase tracking-wide mb-2 text-steel-gray">Bloqueo manual</p>
        <p class="text-[14px] font-semibold truncate text-midnight-indigo">{{ target.label || 'Bloqueado' }}</p>
        <p class="text-[12px] mt-1 text-slate-blue">
          {{ target.date }} &middot; {{ target.startTime }} &ndash; {{ target.endTime }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button type="button" (click)="closeDeleteBlockModal()"
         
          class="flex-1 rounded-xl border px-4 py-2.5 text-[13px] font-semibold transition-colors focus:outline-none border-pale-gray text-slate-blue">
          Cancelar
        </button>
        <button type="button" (click)="confirmDeleteBlock()" [disabled]="deleteSaving()"
         
          class="flex-1 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity disabled:opacity-60 disabled:cursor-wait focus:outline-none bg-red-600">
          @if (deleteSaving()) { Eliminando&hellip; } @else { Eliminar }
        </button>
      </div>

    </div>
  </div>
}

<!-- ── Release Fixed-Block Modal ───────────────────────────────────────── -->
@if (releaseTarget(); as target) {
  <div
      
       (click)="closeReleaseModal()" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 tfr-modal-overlay">

    <div
        
         (click)="$event.stopPropagation()" class="bg-white rounded-2xl border w-full max-w-sm px-6 py-6 border-pale-gray shadow-tfr-sm">

      <!-- Header -->
      <div class="flex items-start justify-between mb-5">
        <div class="min-w-0">
          <h3 class="text-[15px] font-bold tracking-tight text-midnight-indigo">Liberar este hueco</h3>
          <p class="text-[11px] mt-0.5 text-steel-gray">Solo en esta fecha &mdash; el bloqueo recurrente sigue en el resto de d&iacute;as.</p>
        </div>
        <button type="button" (click)="closeReleaseModal()"
         
          class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none shrink-0 ml-3 border-pale-gray text-slate-blue">
          <lucide-icon name="x" [size]="14" />
        </button>
      </div>

      <!-- Summary card -->
      <div class="rounded-xl border p-4 mb-5 border-pale-gray bg-cloud-mist">
        <p class="text-[11px] font-semibold uppercase tracking-wide mb-2 text-steel-gray">Bloqueo recurrente</p>
        <p class="text-[14px] font-semibold truncate text-midnight-indigo">{{ target.label || 'No disponible' }}</p>
        <p class="text-[12px] mt-1 text-slate-blue">
          {{ target.date }} &middot; {{ target.startTime }} &ndash; {{ target.endTime }}
        </p>
      </div>

      <p class="text-[12px] leading-relaxed mb-5 text-slate-blue">
        Al liberar, el hueco quedar&aacute; disponible para reservas en esa fecha. El bloqueo fijo o de temporada seguir&aacute; aplicando en las dem&aacute;s fechas del rango.
      </p>

      <!-- Actions -->
      <div class="flex gap-3">
        <button type="button" (click)="closeReleaseModal()"
         
          class="flex-1 rounded-xl border px-4 py-2.5 text-[13px] font-semibold transition-colors focus:outline-none border-pale-gray text-slate-blue">
          Cancelar
        </button>
        <button type="button" (click)="saveRelease()" [disabled]="releaseSaving()"
         
          class="flex-1 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity disabled:opacity-60 disabled:cursor-wait focus:outline-none bg-midnight-indigo">
          @if (releaseSaving()) { Liberando&hellip; } @else { Liberar }
        </button>
      </div>

    </div>
  </div>
}
